import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Loader2, RefreshCw, Save, Dog, Cat, MessageSquareHeart } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BuddyCard = ({ buddy, password, onSaved }) => {
  const [text, setText] = useState((buddy.lines || []).join('\n'));
  const [saving, setSaving] = useState(false);
  const dirty = text !== (buddy.lines || []).join('\n');

  const save = async () => {
    setSaving(true);
    try {
      const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
      const res = await axios.patch(
        `${API}/admin/presence-lines/${encodeURIComponent(buddy.slug)}?password=${encodeURIComponent(password)}`,
        { lines }
      );
      toast.success(`Saved ${buddy.name}'s lines`);
      onSaved(buddy.slug, res.data.lines);
      setText(res.data.lines.join('\n'));
    } catch (err) {
      toast.error('Save failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const count = text.split('\n').map((l) => l.trim()).filter(Boolean).length;

  return (
    <div className="rounded-2xl bg-zinc-900/70 border border-white/10 p-5" data-testid={`lines-card-${buddy.slug}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-white">
          {buddy.species === 'cat' ? <Cat className="w-4 h-4 text-emerald-300" /> : <Dog className="w-4 h-4 text-emerald-300" />}
          <span className="font-heading font-bold text-lg">{buddy.name}</span>
          <span className="text-xs text-zinc-500">{count} line{count === 1 ? '' : 's'}</span>
        </div>
        <button
          onClick={save}
          disabled={saving || !dirty}
          data-testid={`lines-save-${buddy.slug}`}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-400 text-[#08130d] text-xs font-bold hover:scale-[1.03] transition-transform disabled:opacity-40 disabled:hover:scale-100"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
        </button>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={Math.max(4, count + 1)}
        placeholder="One comfort line per row — they play in this order while a user vents."
        data-testid={`lines-textarea-${buddy.slug}`}
        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-emerald-400/50 outline-none resize-y leading-relaxed"
      />
    </div>
  );
};

const LinesAdminPage = () => {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [pwError, setPwError] = useState('');
  const [loading, setLoading] = useState(false);
  const [buddies, setBuddies] = useState([]);

  const load = async (pw) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/presence-lines?password=${encodeURIComponent(pw)}`);
      setBuddies(res.data.dogs || []);
      setAuthed(true);
      setPwError('');
    } catch (err) {
      if (err.response?.status === 401) { setPwError('Wrong password'); setAuthed(false); }
      else setPwError('Failed to load: ' + (err.message || 'unknown'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e) => { e.preventDefault(); if (password) load(password); };
  const onSaved = (slug, lines) => setBuddies((b) => b.map((x) => (x.slug === slug ? { ...x, lines } : x)));

  if (!authed) {
    return (
      <div className="min-h-screen bg-vent-bg bg-pattern flex items-center justify-center px-5">
        <form onSubmit={handleLogin} className="w-full max-w-sm bg-zinc-900 border border-emerald-400/20 rounded-2xl p-6" data-testid="lines-login-form">
          <div className="flex items-center gap-2 mb-4 text-white">
            <Lock className="w-5 h-5 text-emerald-400" />
            <h1 className="font-heading font-bold text-xl">Comfort Lines Editor</h1>
          </div>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            data-testid="lines-password-input"
            className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus:border-emerald-400/50 outline-none mb-3"
          />
          {pwError && <p className="text-sm text-red-400 mb-3" data-testid="lines-pw-error">{pwError}</p>}
          <button type="submit" disabled={loading} data-testid="lines-login-btn" className="w-full py-3 rounded-xl bg-emerald-400 text-[#08130d] font-heading font-bold hover:scale-[1.02] transition-transform disabled:opacity-60 flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Enter'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vent-bg bg-pattern px-4 sm:px-6 py-10" data-testid="lines-dashboard">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading font-bold text-3xl text-white flex items-center gap-2">
              <MessageSquareHeart className="w-6 h-6 text-emerald-400" /> Comfort Lines
            </h1>
            <p className="text-zinc-500 text-sm mt-1">Edit what each buddy "says" while someone vents. Saves instantly — no redeploy.</p>
          </div>
          <button onClick={() => load(password)} disabled={loading} data-testid="lines-refresh-btn" className="flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-800 text-white text-sm hover:bg-zinc-700 transition-colors disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
        </div>

        <div className="space-y-4">
          {buddies.map((b) => (
            <BuddyCard key={b.slug} buddy={b} password={password} onSaved={onSaved} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default LinesAdminPage;
